import { ApolloError } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback } from 'react';
import { useAlert } from '@shared/components/Modal';
import { i18n } from '@shared/util/i18n';
import styles from './subtableRowLimit.module.css';

/** The backend's per-parent row limit for each subtable. */
export const SUBTABLE_ROW_LIMIT = 25;

const ROW_LIMIT_CODE = 'WORKFLOW_ENTITY_SUBTABLE_ROW_LIMIT_EXCEEDED';

interface RowLimitApiError {
  code?: string;
  additionalData?: { limit?: number };
}

/**
 * The limit from a create rejected because the parent already holds the maximum number of rows
 * (someone else added the last one while the add-row form was open); undefined for any other
 * error.
 */
export function getSubtableRowLimitFromError(error: unknown): number | undefined {
  if (!(error instanceof ApolloError)) {
    return undefined;
  }
  const coastApiError = error.graphQLErrors
    .map(graphQLError => graphQLError.extensions?.coastApiError as RowLimitApiError | undefined)
    .find(apiError => apiError?.code === ROW_LIMIT_CODE);
  if (isNil(coastApiError)) {
    return undefined;
  }
  return coastApiError.additionalData?.limit ?? SUBTABLE_ROW_LIMIT;
}

/** Tells the user the subtable can't take another row. */
export function useSubtableRowLimitAlert(): (limit?: number) => Promise<void> {
  const alert = useAlert();
  return useCallback(
    (limit = SUBTABLE_ROW_LIMIT) => {
      return alert({
        title: i18n('card.blocks.subtableInput.rowLimitTitle'),
        message: i18n('card.blocks.subtableInput.rowLimitMessage', { limit }),
        buttonLabel: i18n('common.ok'),
        className: styles.subtableRowLimitAlert,
      });
    },
    [alert],
  );
}
